// src/pages/reports/UpiQrReportsPage.jsx
//
// Drop into:  src/pages/reports/UpiQrReportsPage.jsx
// Add route:  <Route path="/upi-qr-reports" element={<UpiQrReportsPage />} />
// Add config: export upiQrReportConfig from config/tableConfigs.js  (see bottom of file)
//
// Endpoints used:
//   GET  https://pro.mokshiq.in/upi_qr_reports_data?user={id}&filter_date=&end_date=
//   POST https://pro.mokshiq.in/admin_update_upi_qr/
//        body: report_id, status, utr, txn_id

import React, { useState, useMemo } from "react";
import DataTable    from "components/DataTable";
import PageWrapper  from "components/PageWrapper";
import { useTableData }  from "hooks/useTableData";
import { useSession }    from "hooks/useSession";
import { fetchWrapper }  from "utils/apiFetch";

// ─────────────────────────────────────────────────────────────────────────────
// STATUS BADGE
// ─────────────────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    pending: { bg: "#fff8e1", color: "#f59f00", border: "#ffe082", label: "Pending"  },
    success: { bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7", label: "Success"  },
    failed:  { bg: "#fce4ec", color: "#c62828", border: "#ef9a9a", label: "Failed"   },
  };
  const s = map[status?.toLowerCase()] ?? map.pending;
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px",
      borderRadius: 20, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      {s.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// INFO ROW — used inside modal detail card
// ─────────────────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value }) => (
  <div style={{
    display: "flex", justifyContent: "space-between",
    alignItems: "center", padding: "6px 0",
    borderBottom: "1px solid #f0f0f0", fontSize: 13,
  }}>
    <span style={{ color: "#888", minWidth: 130 }}>{label}</span>
    <span style={{ fontWeight: 600, color: "#1a1a1a", textAlign: "right" }}>
      {value || "—"}
    </span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN UPDATE MODAL
// Calls POST /admin_update_upi_qr/
// ─────────────────────────────────────────────────────────────────────────────
const AdminUpdateModal = ({ isOpen, onClose, report, onSuccess }) => {
  const [status,  setStatus]  = useState("success");
  const [utr,     setUtr]     = useState("");
  const [txnId,   setTxnId]   = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [done,    setDone]    = useState(false);

  if (!isOpen || !report) return null;

  const reset = () => {
    setUtr(""); setTxnId(""); setError("");
    setStatus("success"); setDone(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    if (!utr.trim()) { setError("UTR number is required."); return; }
    setError(""); setLoading(true);

    try {
      const fd = new FormData();
      fd.append("report_id", report.id);
      fd.append("status",    status);
      fd.append("utr",       utr.trim());
      fd.append("txn_id",    txnId.trim());

      const res = await fetchWrapper(
        "https://pro.mokshiq.in/admin_update_upi_qr/",
        { method: "POST", body: fd }
      );

      if (res?.status) {
        setDone(true);
        setTimeout(() => { onSuccess(report.id, status); handleClose(); }, 1400);
      } else {
        setError(res?.message || "Update failed. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isSuccess = status === "success";

  return (
    // Backdrop
    <div
      onClick={handleClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(10,10,20,0.55)",
        backdropFilter: "blur(3px)",
        zIndex: 5000,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      {/* Modal card */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16,
          width: "100%", maxWidth: 520,
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
          overflow: "hidden",
          fontFamily: "'Segoe UI', sans-serif",
        }}
      >
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #4a148c 0%, #6a1b9a 100%)",
          padding: "20px 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
              Update UPI QR Payment
            </div>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 12, marginTop: 2 }}>
              Transaction #{report.id}
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: "rgba(255,255,255,0.15)", border: "none",
              borderRadius: 8, width: 32, height: 32,
              color: "#fff", fontSize: 18, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >×</button>
        </div>

        <div style={{ padding: 24 }}>

          {/* ── Success / Done state ── */}
          {done ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <div style={{
                width: 64, height: 64, borderRadius: "50%",
                background: isSuccess ? "#e8f5e9" : "#fce4ec",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 14px", fontSize: 30,
              }}>
                {isSuccess ? "✓" : "✗"}
              </div>
              <div style={{
                fontWeight: 700, fontSize: 17,
                color: isSuccess ? "#2e7d32" : "#c62828",
              }}>
                Marked as {isSuccess ? "Success" : "Failed"}
              </div>
              <div style={{ color: "#888", fontSize: 13, marginTop: 5 }}>
                Wallets have been {isSuccess ? "confirmed" : "reversed"}.
              </div>
            </div>
          ) : (
            <>
              {/* Transaction summary */}
              <div style={{
                background: "#f8f9fc", borderRadius: 10,
                padding: "12px 16px", marginBottom: 20,
                border: "1px solid #ebebeb",
              }}>
                {report["BC Name"] && (
                  <InfoRow label="BC Name"       value={report["BC Name"]} />
                )}
                <InfoRow label="Merchant Name"   value={report.merchant_name} />
                <InfoRow label="UPI VPA"         value={report.merchant_vpa} />
                <InfoRow label="Note"            value={report.note} />
                <InfoRow label="Amount"
                  value={report.amount ? `₹${Number(report.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : null}
                />
                <InfoRow label="Reference"       value={report.order_id} />
                <div style={{
                  display: "flex", justifyContent: "space-between",
                  alignItems: "center", paddingTop: 8, fontSize: 13,
                }}>
                  <span style={{ color: "#888" }}>Current Status</span>
                  <StatusBadge status={report.status} />
                </div>
              </div>

              {/* Status selector */}
              <div style={{ marginBottom: 18 }}>
                <label style={{
                  fontWeight: 600, fontSize: 13, color: "#444",
                  display: "block", marginBottom: 8,
                }}>
                  New Status <span style={{ color: "#e53935" }}>*</span>
                </label>
                <div style={{ display: "flex", gap: 10 }}>
                  {["success", "failed"].map(s => (
                    <div
                      key={s}
                      onClick={() => setStatus(s)}
                      style={{
                        flex: 1, textAlign: "center", padding: "11px 0",
                        borderRadius: 10, cursor: "pointer",
                        border: `2px solid ${status === s
                          ? (s === "success" ? "#43a047" : "#e53935")
                          : "#e0e0e0"}`,
                        background: status === s
                          ? (s === "success" ? "#e8f5e9" : "#fce4ec")
                          : "#fafafa",
                        color: status === s
                          ? (s === "success" ? "#2e7d32" : "#c62828")
                          : "#9e9e9e",
                        fontWeight: 700, fontSize: 14,
                        transition: "all 0.15s",
                        userSelect: "none",
                      }}
                    >
                      {s === "success" ? "✓ Approve" : "✗ Reject"}
                    </div>
                  ))}
                </div>
              </div>

              {/* UTR */}
              <div style={{ marginBottom: 14 }}>
                <label style={{
                  fontWeight: 600, fontSize: 13, color: "#444",
                  display: "block", marginBottom: 6,
                }}>
                  UTR Number <span style={{ color: "#e53935" }}>*</span>
                </label>
                <input
                  value={utr}
                  onChange={e => setUtr(e.target.value)}
                  placeholder="Enter UTR / bank reference number"
                  style={{
                    width: "100%", padding: "10px 14px",
                    border: `1.5px solid ${error && !utr ? "#e53935" : "#dde1e7"}`,
                    borderRadius: 9, fontSize: 14, outline: "none",
                    transition: "border-color 0.15s", boxSizing: "border-box",
                  }}
                  onFocus={e => (e.target.style.borderColor = "#6a1b9a")}
                  onBlur={e  => (e.target.style.borderColor = "#dde1e7")}
                />
              </div>

              {/* External TXN ID */}
              <div style={{ marginBottom: 18 }}>
                <label style={{
                  fontWeight: 600, fontSize: 13, color: "#444",
                  display: "block", marginBottom: 6,
                }}>
                  External TXN ID{" "}
                  <span style={{ color: "#aaa", fontWeight: 400, marginLeft: 6 }}>
                    (optional)
                  </span>
                </label>
                <input
                  value={txnId}
                  onChange={e => setTxnId(e.target.value)}
                  placeholder="Bank / gateway transaction ID"
                  style={{
                    width: "100%", padding: "10px 14px",
                    border: "1.5px solid #dde1e7",
                    borderRadius: 9, fontSize: 14, outline: "none",
                    transition: "border-color 0.15s", boxSizing: "border-box",
                  }}
                  onFocus={e => (e.target.style.borderColor = "#6a1b9a")}
                  onBlur={e  => (e.target.style.borderColor = "#dde1e7")}
                />
              </div>

              {/* Warning — failed reversal notice */}
              {status === "failed" && (
                <div style={{
                  background: "#fff8e1", border: "1px solid #ffe082",
                  borderRadius: 9, padding: "10px 14px",
                  fontSize: 13, color: "#e65100", marginBottom: 16,
                  display: "flex", gap: 8, alignItems: "flex-start",
                }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
                  <span>
                    Rejecting this payment will <strong>reverse all wallet deductions</strong>{" "}
                    for every level in the hierarchy. This action <strong>cannot be undone</strong>.
                  </span>
                </div>
              )}

              {/* Error banner */}
              {error && (
                <div style={{
                  background: "#fce4ec", border: "1px solid #ef9a9a",
                  borderRadius: 9, padding: "10px 14px",
                  fontSize: 13, color: "#b71c1c", marginBottom: 16,
                }}>
                  {error}
                </div>
              )}

              {/* Actions */}
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={handleClose}
                  disabled={loading}
                  style={{
                    flex: 1, padding: "11px 0", borderRadius: 9,
                    border: "1.5px solid #dde1e7", background: "#fff",
                    color: "#555", fontWeight: 600, fontSize: 14, cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{
                    flex: 2, padding: "11px 0", borderRadius: 9, border: "none",
                    background: isSuccess
                      ? "linear-gradient(135deg, #388e3c, #43a047)"
                      : "linear-gradient(135deg, #c62828, #e53935)",
                    color: "#fff", fontWeight: 700, fontSize: 14,
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.8 : 1,
                    display: "flex", alignItems: "center",
                    justifyContent: "center", gap: 8,
                  }}
                >
                  {loading && (
                    <span style={{
                      width: 16, height: 16, borderRadius: "50%",
                      border: "2px solid rgba(255,255,255,0.4)",
                      borderTopColor: "#fff",
                      animation: "upiSpin 0.7s linear infinite",
                      display: "inline-block",
                    }} />
                  )}
                  {loading
                    ? "Updating…"
                    : isSuccess ? "✓ Approve Payment" : "✗ Reject Payment"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`@keyframes upiSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
const UpiQrReportsPage = () => {
  const { userid, role } = useSession();

  const isAdmin     = role === "Admin"      || role === "Main Admin";
  const isWhiteLabel = role === "White label";

  const [showModal,       setShowModal]       = useState(false);
  const [selectedReport,  setSelectedReport]  = useState(null);

  // ── Default: today's date range ──
  const getTodayRange = () => {
    const t = new Date().toISOString().split("T")[0];
    return { start: t, end: t };
  };
  const { start, end } = getTodayRange();

  const [apiUrl, setApiUrl] = useState(
    `https://pro.mokshiq.in/upi_qr_reports_data?user=${userid}&filter_date=${start}&end_date=${end}`
  );

  const tableData = useTableData(apiUrl, 0, upiQrReportConfig);

  // ── Refetch after modal update ──
  const handleUpdateSuccess = (_reportId, _newStatus) => {
    setApiUrl(prev => {
      const base = prev.split("&_r=")[0];
      return `${base}&_r=${Date.now()}`;
    });
  };

  // ── Date filter from PageWrapper ──
  const handleDateFilter = (s, e) => {
    setApiUrl(
      `https://pro.mokshiq.in/upi_qr_reports_data?user=${userid}&filter_date=${s}&end_date=${e}`
    );
  };

  // ── Edit button — admin only, pending only ──
  const handleEdit = (report) => {
    if (!isAdmin) return;
    if (report.status?.toLowerCase() !== "pending") {
      alert(`This transaction is already "${report.status}" and cannot be updated.`);
      return;
    }
    setSelectedReport(report);
    setShowModal(true);
  };

  // ── Role-based column visibility (mirrors OfflineBBPSReportsPage exactly) ──
  const filteredConfig = useMemo(() => {
    const base = {
      ...upiQrReportConfig,
      columns: [...upiQrReportConfig.columns],
      actions: {
        ...upiQrReportConfig.actions,
        edit: isAdmin,
      },
    };

    const roleColumns = {
      Retailer: [
        "id", "order_id", "merchant_name", "merchant_vpa", "note",
        "amount", "status",
        "Opening Balance", "Commission", "TDS", "Closing Balance",
        "created_at",
      ],
      "API Partner": [
        "id", "order_id", "merchant_name", "merchant_vpa", "note",
        "amount", "status",
        "Opening Balance", "Commission", "TDS", "Closing Balance",
        "created_at",
      ],
      Distributer: [
        "id", "order_id", "merchant_name", "merchant_vpa",
        "amount", "status", "commission", "tds", "created_at",
      ],
      "Master Distributor": [
        "id", "order_id", "merchant_name", "merchant_vpa",
        "amount", "status", "commission", "tds", "created_at",
      ],
      "White label": [
        "id", "BC Name", "Phone", "order_id",
        "merchant_name", "merchant_vpa", "note",
        "amount", "status",
        "Opening Balance", "Commission", "TDS", "Closing Balance",
        "created_at",
      ],
      Admin: [
        "id", "BC Name", "Phone", "order_id", "txn_id",
        "merchant_name", "merchant_vpa", "note",
        "amount", "status",
        "Opening Balance",    "Commission",      "TDS",    "Closing Balance",
        "Opening Balance BC", "BC Commission",   "BC TDS", "Closing balance BC",
        "UTR", "Description", "created_at",
      ],
      "Main Admin": [
        "id", "BC Name", "Phone", "order_id", "txn_id",
        "merchant_name", "merchant_vpa", "note",
        "amount", "status",
        "Opening Balance",         "Commission",          "TDS",        "Closing Balance",
        "Opening Balance BC",      "BC Commission",       "BC TDS",     "Closing balance BC",
        "Opening Balance MAdmin",  "Commission MAdmin",   "TDS MAdmin", "Closing balance MAdmin",
        "UTR", "Description", "created_at",
      ],
    };

    const allowed = roleColumns[role] ?? [];
    base.columns = base.columns.filter(c => allowed.includes(c.key));
    return base;
  }, [role, isAdmin]);

  return (
    <div>
      <PageWrapper
        config={{ ...upiQrReportConfig, title: "UPI QR Payment Reports" }}
        onDateFilter={handleDateFilter}
      >
        {/* Admin hint banner */}
        {isAdmin && (
          <div style={{
            background: "linear-gradient(90deg, #f3e5f5, #fce4ec)",
            border: "1px solid #ce93d8",
            borderRadius: 10, padding: "10px 18px",
            marginBottom: 14, fontSize: 13, color: "#6a1b9a",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ fontSize: 16 }}>ℹ️</span>
            Click <strong>Edit</strong> on any <strong>Pending</strong> UPI QR transaction to{" "}
            <strong>Approve</strong> or <strong>Reject</strong> it.
          </div>
        )}

        <DataTable
          config={filteredConfig}
          tableData={tableData}
          onView={() => {}}
          onEdit={isAdmin ? handleEdit : undefined}
          onDelete={() => {}}
          onExport={() => {}}
          onDeleteSelected={() => {}}
        />
      </PageWrapper>

      {/* Admin update modal */}
      {isAdmin && (
        <AdminUpdateModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setSelectedReport(null); }}
          report={selectedReport}
          onSuccess={handleUpdateSuccess}
        />
      )}
    </div>
  );
};

export default UpiQrReportsPage;

// ─────────────────────────────────────────────────────────────────────────────
// TABLE CONFIG
// Add this export to: src/config/tableConfigs.js
// ─────────────────────────────────────────────────────────────────────────────
//
export const upiQrReportConfig = {
  title:      "UPI QR Reports",
  apiKey:     "data",
  searchKeys: ["merchant_vpa", "merchant_name", "order_id", "txn_id", "amount", "status"],
  actions: { view: false, edit: true, delete: false, export: true },
  columns: [
    { key: "id",                    label: "ID"                     },
    { key: "BC Name",               label: "BC Name"                },
    { key: "Phone",                 label: "Phone"                  },
    { key: "order_id",              label: "Reference"              },
    { key: "txn_id",                label: "TXN ID"                 },
    { key: "merchant_name",         label: "Merchant"               },
    { key: "merchant_vpa",          label: "UPI VPA"                },
    { key: "note",                  label: "Note"                   },
    { key: "amount",                label: "Amount (₹)"             },
    { key: "status",                label: "Status",   render: (v) => <StatusBadge status={v} /> },
    { key: "Opening Balance",       label: "Opening Bal."           },
    { key: "Commission",            label: "Commission"             },
    { key: "TDS",                   label: "TDS"                    },
    { key: "Closing Balance",       label: "Closing Bal."           },
    { key: "commission",            label: "Commission (D)"         },
    { key: "tds",                   label: "TDS (D)"                },
    { key: "Opening Balance BC",    label: "Opening Bal. BC"        },
    { key: "BC Commission",         label: "BC Commission"          },
    { key: "BC TDS",                label: "BC TDS"                 },
    { key: "Closing balance BC",    label: "Closing Bal. BC"        },
    { key: "Opening Balance MAdmin",label: "Opening Bal. MAdmin"    },
    { key: "Commission MAdmin",     label: "Commission MAdmin"      },
    { key: "TDS MAdmin",            label: "TDS MAdmin"             },
    { key: "Closing balance MAdmin",label: "Closing Bal. MAdmin"    },
    { key: "UTR",                   label: "UTR"                    },
    { key: "Description",           label: "Description"            },
    { key: "created_at",            label: "Date & Time"            },
  ],
};